/**
 * Time Analysis Tab Component
 * Shows time-based insights including daily breakdown and hourly heatmap
 */

export default function TimeAnalysisTab({ 
  timeInsights,
  formatCurrency
}) {
  if (!timeInsights) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        No time insights available
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-analysis-tab">
      {/* Key Insights */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Busiest Day</p>
          <p className="text-xl font-bold text-foreground">{timeInsights.insights?.busiest_day || 'N/A'}</p>
          <p className="text-xs text-green-600 dark:text-green-400">
            {timeInsights.insights?.busiest_day_orders || 0} orders
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Slowest Day</p>
          <p className="text-xl font-bold text-foreground">{timeInsights.insights?.slowest_day || 'N/A'}</p>
          <p className="text-xs text-muted-foreground">
            {timeInsights.insights?.slowest_day_orders || 0} orders
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Peak Hour</p>
          <p className="text-xl font-bold text-foreground">{timeInsights.insights?.peak_hour || 'N/A'}</p>
          <p className="text-xs text-green-600 dark:text-green-400">
            {timeInsights.insights?.peak_hour_orders || 0} orders
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Rush Hours</p>
          <p className="text-lg font-bold text-foreground">
            {timeInsights.insights?.rush_hours?.join(', ') || 'N/A'}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Breakdown */}
        <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4">Orders by Day of Week</h3>
          <div className="space-y-3">
            {timeInsights.daily_breakdown?.map((day) => {
              const maxOrders = Math.max(...(timeInsights.daily_breakdown?.map(d => d.orders) || [1]));
              return (
                <div key={day.day}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground">{day.day}</span>
                    <span className="text-muted-foreground">
                      {day.orders} orders • {formatCurrency(day.revenue)}
                    </span>
                  </div>
                  <div className="h-3 bg-muted dark:bg-muted/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${maxOrders > 0 ? (day.orders / maxOrders) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {!timeInsights.daily_breakdown?.length && (
              <p className="text-center text-muted-foreground py-4">No daily data available</p>
            )}
          </div>
        </div>

        {/* Hourly Heatmap */}
        <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
          <h3 className="font-medium text-foreground mb-4">Orders by Hour</h3>
          {timeInsights.hourly_breakdown?.length > 0 ? (
            <>
              <div className="grid grid-cols-12 gap-1">
                {timeInsights.hourly_breakdown.map((hour) => {
                  const maxOrders = Math.max(...(timeInsights.hourly_breakdown?.map(h => h.orders) || [1]));
                  const intensity = maxOrders > 0 ? hour.orders / maxOrders : 0;
                  return (
                    <div 
                      key={hour.hour}
                      className="aspect-square rounded flex items-center justify-center text-xs cursor-pointer transition-transform hover:scale-110"
                      style={{ 
                        backgroundColor: intensity > 0.5 
                          ? `rgba(59, 130, 246, ${intensity * 0.8 + 0.2})`
                          : intensity > 0 
                            ? `rgba(59, 130, 246, ${intensity * 0.5 + 0.1})`
                            : 'rgba(156, 163, 175, 0.1)',
                        color: intensity > 0.5 ? 'white' : 'inherit'
                      }}
                      title={`${hour.label}: ${hour.orders} orders`}
                    >
                      {hour.hour % 6 === 0 ? hour.hour : ''}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>12am</span>
                <span>6am</span>
                <span>12pm</span>
                <span>6pm</span>
                <span>12am</span>
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-4">No hourly data available</p>
          )}
        </div>
      </div>
    </div>
  );
}
